import { useCreateStoryboard } from "@/hooks/storyboards/useCreateStoryboard";
import { useInitializeStoryboard } from "@/hooks/storyboards/useInitializeStoryboards";
import { useDevServerStore } from "@/store/zustand/devServerStore";
import { useLoadCodebase, useRunTerminalCommand } from "@/utils/devServerUtils";
import {
  getAxiosErorrMessage,
  getNewStoryboardPosition,
  storyboardOverlapsWithAnyOthers,
  useActiveCanvas,
  useActiveProject,
  useCanvasId,
} from "@/utils/utils";
import { useSession } from "@clerk/clerk-react";
import axios from "axios";
import { AssistantInstruction } from "./messages";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { Storyboard } from "@/utils/types";
import { createDummyComponent } from "@/components/AppHeader/utils";
import { ReactFramework } from "@/utils/templateProjects";
import { EngSpecFileDiff } from "../../EngSpec/EngSpec";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { usePagesStore } from "@/store/zustand/pagesStore";

export const useExecuteDbCommand = () => {
  const canvasId = useCanvasId();
  const runTerminalCommand = useRunTerminalCommand();
  const loadCodebase = useLoadCodebase();
  const addTerminalErrors = useDevServerStore(
    (state) => state.addTerminalErrors,
  );

  const runConvexSync = async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    try {
      const { success, error } = await runTerminalCommand({
        command: `npx convex login --device-name ${uuidv4()} --no-open`,
      });
      // runTerminalCommand handles addTerminalErrors
      loadCodebase();
      if (success) {
        return { success: true };
      } else {
        return { success: false, error };
      }
    } catch (e) {
      return { success: false, error: getAxiosErorrMessage(e) };
    }
  };

  const { session } = useSession();
  const runSqlMigration = async (
    filename: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      // fetch a new supabase JWT in case AI gen took longer than ttl
      const freshSupabaseJWT = await session?.getToken();

      const response = await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/db/supabase/migration`,
        {
          relativeMigrationFilePath: filename,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${freshSupabaseJWT}`,
          },
        },
      );
      return { success: true };
    } catch (e) {
      const errorMessage = getAxiosErorrMessage(e);
      addTerminalErrors([
        {
          message: errorMessage,
          filename: filename,
          stack: "",
          count: 1,
        },
      ]);
      return { success: false, error: errorMessage };
    }
  };

  const deployEdgeFunction = async (
    filename: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      // fetch a new supabase JWT in case AI gen took longer than ttl
      const freshSupabaseJWT = await session?.getToken();

      const response = await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/db/supabase/edgeFunctionDeployment`,
        {
          relativeFunctionFilePath: filename,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${freshSupabaseJWT}`,
          },
        },
      );

      return { success: true };
    } catch (e) {
      const errorMessage = getAxiosErorrMessage(e);
      // updateMessageContext({ hadError: true });
      toast.error("Failed to deploy edge function: " + errorMessage);
      addTerminalErrors([
        {
          message: errorMessage,
          filename: filename,
          stack: "",
          count: 1,
        },
      ]);
      return { success: false, error: errorMessage };
    }
  };

  return { runConvexSync, runSqlMigration, deployEdgeFunction };
};

export const usePreloadStoryboard = () => {
  const initializeStoryboard = useInitializeStoryboard();
  const activeCanvas = useActiveCanvas();
  const storyboardPromises: Promise<Storyboard | null>[] = [];
  const pendingStoryboards: Storyboard[] = []; // Track storyboards not yet in the canvas
  const project = useActiveProject();
  const createStoryboard = useCreateStoryboard();
  const canvasId = useCanvasId();
  const selectedPageId = usePagesStore(
    (state) => state.selectedPageId[canvasId],
  );

  const preloadStoryboard = (
    params: {
      storyboardName: string;
      width?: number;
      height?: number;
      x?: number;
      y?: number;
    },
    withContents?: string,
  ) => {
    const { storyboardName, width, height, x, y } = params;
    if (pendingStoryboards.find((sb) => sb.name === storyboardName)) {
      return;
    }

    // Create a storyboard of the given dimensions at a valid position
    const storyboard = initializeStoryboard(
      x || 0,
      y || 0,
      width || 100,
      height || 100,
      uuidv4(),
      storyboardName,
    );
    const activeStoryboards = activeCanvas?.storyboards || [];
    const allStoryboards = activeStoryboards.concat(pendingStoryboards);
    const overlaps = storyboardOverlapsWithAnyOthers(
      storyboard,
      allStoryboards,
    );
    if (overlaps) {
      const newPosition = getNewStoryboardPosition(
        storyboard.width,
        storyboard.height,
        allStoryboards,
      );
      storyboard.x = newPosition.x;
      storyboard.y = newPosition.y;
    }

    pendingStoryboards.push(storyboard);

    let contents = "<div/>";
    if (project?.framework === ReactFramework.EXPO) {
      contents = "<View/>";
    }

    const sbContents =
      withContents ||
      createDummyComponent(
        contents,
        project?.framework as ReactFramework,
        storyboardName,
      );

    const promise = createStoryboard({
      storyboard,
      pageId: selectedPageId || "",
      fileContentsToCreateWith: sbContents,
      scrollToStoryboard: true,
      actionText: "AI Change",
    });
    storyboardPromises.push(promise);
    return promise;
  };

  const getPreloadedStoryboards = async () => {
    const storyboards = await Promise.all(storyboardPromises);
    return storyboards.filter(Boolean) as Exclude<
      (typeof storyboards)[number],
      null | undefined
    >[];
  };

  return { preloadStoryboard, getPreloadedStoryboards };
};
